import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Comparison, RuleInterface, StringListContains, StringListContainsAll } from '@vendasta/automata';
import { FieldSchema, ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';

export type CustomDataUpdateType = Comparison.StringListContains | Comparison.StringListContainsAll;

@Component({
  imports: [
    CommonModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
    MatRadioModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatIconModule,
    TranslateModule,
  ],
  selector: 'automata-custom-data-updated-panel',
  template: `
    <span class="description">
      {{ panelTitle | translate }}
    </span>
    <div class="form" [formGroup]="form">
      <glxy-form-field>
        <glxy-label>
          {{ 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.UPDATE_TYPE' | translate }}
        </glxy-label>
        <mat-radio-group formControlName="updateType">
          <mat-radio-button [value]="Comparison.StringListContains">
            {{ 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.ANY_FIELDS' | translate }}
            <extended>
              {{ 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.ANY_FIELDS_DESCRIPTION' | translate }}
            </extended>
          </mat-radio-button>
          <mat-radio-button [value]="Comparison.StringListContainsAll">
            {{ 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.ALL_FIELDS' | translate }}
            <extended>
              {{ 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.ALL_FIELDS_DESCRIPTION' | translate }}
            </extended>
          </mat-radio-button>
        </mat-radio-group>
      </glxy-form-field>

      <glxy-form-field
        [showLabel]="true"
        [suffixIcon]="'search'"
        *ngIf="filteredFieldSchemas$ | async as filteredFieldSchemas; else loading"
      >
        <mat-chip-grid
          #chipList
          [attr.aria-label]="'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.SELECT_CUSTOM_FIELDS' | translate"
          *ngIf="selectedFields$ | async as selectedFields"
        >
          <mat-chip-row *ngFor="let field of selectedFields" (removed)="removeField(field.fieldId)">
            {{ field.fieldName }}
            <button matChipRemove>
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip-row>
          <input
            #fieldInput
            type="text"
            [placeholder]="'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL.SELECT_CUSTOM_FIELDS' | translate"
            matInput
            formControlName="fieldSearch"
            [matAutocomplete]="auto"
            [matChipInputFor]="chipList"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            (matChipInputTokenEnd)="addField($event)"
          />
        </mat-chip-grid>
        <mat-autocomplete autoActiveFirstOption #auto="matAutocomplete" (optionSelected)="selectedField($event)">
          <ng-container *ngFor="let schema of filteredFieldSchemas">
            <mat-option [value]="schema.fieldId">
              {{ schema.fieldName }}
            </mat-option>
          </ng-container>
        </mat-autocomplete>
        <glxy-error *ngIf="validationError$ | async as validationError">
          {{ validationError }}
        </glxy-error>
      </glxy-form-field>
    </div>

    <ng-template #loading>
      <div class="side-drawer-shimmer-container">
        <span class="title stencil-shimmer"></span>
        <span class="selector stencil-shimmer"></span>
      </div>
    </ng-template>
  `,
  styleUrls: ['./custom-data-updated-panel.component.scss'],
})
export class CustomDataUpdatedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  @Input() objectType: ObjectType;
  @Input() panelTitle: string;

  @ViewChild('fieldInput') fieldInput: ElementRef<HTMLInputElement>;
  Comparison = Comparison;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedFields$: Observable<FieldSchema[]>;
  selectedFieldIds$$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  validationError$: Observable<string>;
  hasArchivedError = false;

  private fieldSchemas$: Observable<FieldSchema[]>;
  filteredFieldSchemas$: Observable<FieldSchema[]>;

  form: UntypedFormGroup;
  updateTypeForm = new FormControl<CustomDataUpdateType>(Comparison.StringListContains, [Validators.required]);
  fieldSearch = new UntypedFormControl();

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) protected readonly partnerId$: Observable<string>,
    private readonly schemaService: SchemaService,
    private readonly fb: UntypedFormBuilder,
    protected readonly translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.savingEnabled$.next(true);
    this.initializeFormFields(this.triggerChoices?.rules[0]);
    this.form = this.fb.group({
      updateType: this.updateTypeForm,
      fieldSearch: this.fieldSearch,
    });

    const fieldIDsFromRule = this.triggerChoices?.rules[0]?.value
      ? JSON.parse(this.triggerChoices?.rules[0]?.value)
      : [];
    this.partnerId$.pipe(take(1)).subscribe((partnerId) => {
      this.setFieldSchemasObservables(fieldIDsFromRule, partnerId);
    });
  }

  addField(event: MatChipInputEvent): void {
    const selectedFieldID = (event.value || '').trim();
    this.addToSelectedFields(selectedFieldID);
    event.chipInput?.clear();
  }

  selectedField(event: MatAutocompleteSelectedEvent): void {
    const selectedFieldID = (event.option.value || '').trim();
    this.addToSelectedFields(selectedFieldID);
    this.fieldInput.nativeElement.value = '';
  }

  removeField(fieldId: string): void {
    const selectedFieldIds = this.selectedFieldIds$$.value;
    const removedFieldIndex = selectedFieldIds.indexOf(fieldId);
    if (removedFieldIndex >= 0) {
      selectedFieldIds.splice(removedFieldIndex, 1);
      this.selectedFieldIds$$.next(selectedFieldIds);
      this.fieldSearch.setValue(null);
    }
  }

  isFormValid(): boolean {
    if (this.selectedFieldIds$$.value.length < 1) {
      return false;
    }
    if (this.hasArchivedError) {
      return false;
    }
    return true;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    const rules: RuleInterface[] = [];

    switch (this.updateTypeForm.value) {
      case Comparison.StringListContains:
        rules.push(
          new StringListContains(
            '{.updated_custom_field_ids}',
            [],
            this.automation.triggerStep?.choices?.rules?.length > 0
              ? this.automation.triggerStep.choices.rules[0].next
              : '',
          ).toRuleInterface(),
        );
        break;
      case Comparison.StringListContainsAll:
        rules.push(
          new StringListContainsAll(
            '{.updated_custom_field_ids}',
            [],
            this.automation.triggerStep?.choices?.rules?.length > 0
              ? this.automation.triggerStep.choices.rules[0].next
              : '',
          ).toRuleInterface(),
        );
        break;
      default:
        console.warn('Update type not supported', this.updateTypeForm.value);
    }

    return rules;
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    rules[0].comparison = this.updateTypeForm.value;
    rules[0].value = JSON.stringify(this.selectedFieldIds$$.value);
    return rules;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  private initializeFormFields(triggerRule: RuleInterface): void {
    if (!triggerRule) {
      return;
    }
    if (triggerRule?.value) {
      try {
        this.selectedFieldIds$$.next(JSON.parse(triggerRule?.value));
      } catch (err) {
        console.error('Error parsing rules for trigger:', err);
        this.selectedFieldIds$$.next([]);
      }
    }
    if (triggerRule?.comparison) {
      this.updateTypeForm.setValue(triggerRule?.comparison as CustomDataUpdateType);
    }
  }

  private setFieldSchemasObservables(fieldIds: string[], partnerId: string): void {
    const archivedFieldSchemas$ = of(fieldIds).pipe(
      switchMap((selectedFieldIds) => {
        if (selectedFieldIds.length < 1) {
          return of({ fieldSchemas: [] });
        }
        return this.schemaService.getMultiFieldSchema(
          this.objectType,
          selectedFieldIds.map((f) => ({ fieldId: f, partnerId: partnerId })),
        );
      }),
      map((response) => response.fieldSchemas),
      map((fieldSchemas) => fieldSchemas.filter((schema) => !!schema.archived)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const availableSchemas$ = this.schemaService.listFieldSchemas(this.objectType, partnerId, false, '', 100).pipe(
      map((response) => response?.fieldSchemas || []),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.fieldSchemas$ = combineLatest([availableSchemas$, archivedFieldSchemas$]).pipe(
      switchMap(([fieldSchemas, archivedFieldSchemas]) => {
        if (archivedFieldSchemas.length > 0) {
          return of([...fieldSchemas, ...archivedFieldSchemas]);
        }
        return of(fieldSchemas);
      }),
    );
    this.selectedFields$ = combineLatest([this.fieldSchemas$, this.selectedFieldIds$$]).pipe(
      map(([fieldSchemas, selectedFieldIds]) => fieldSchemas.filter((f) => selectedFieldIds.includes(f.fieldId))),
      distinctUntilChanged((prev, curr) => prev.length === curr.length),
    );
    const selectedArchivedFields$ = this.selectedFields$.pipe(
      map((selectedFields) => selectedFields.filter((s) => !!s.archived)),
      tap((archivedFields) => (this.hasArchivedError = archivedFields.length > 0)),
    );
    this.validationError$ = this.selectedFields$.pipe(
      withLatestFrom(selectedArchivedFields$),
      switchMap(([selectedFields, archivedFields]) => {
        if (selectedFields.length < 1) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL_ERRORS.REQUIRED');
        }
        if (archivedFields.length > 0) {
          const archivedFieldDisplay = archivedFields.map((f) => f.fieldName).join(', ');
          if (archivedFields.length === 1) {
            return this.translateService.stream(
              'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL_ERRORS.ARCHIVED_FIELD',
              { archivedField: archivedFieldDisplay },
            );
          }
          return this.translateService.stream(
            'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.PANEL_ERRORS.ARCHIVED_FIELDS',
            { archivedFields: archivedFieldDisplay },
          );
        }
        return of('');
      }),
    );
    this.filteredFieldSchemas$ = combineLatest([
      this.fieldSchemas$,
      this.fieldSearch.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fieldSchemas, fieldSearch]) => {
        if (!fieldSearch) {
          return fieldSchemas;
        }
        return fieldSchemas.filter((schema) => schema.fieldName.toLowerCase().includes(fieldSearch.toLowerCase()));
      }),
      withLatestFrom(this.selectedFields$),
      map(([filteredBySearch, selectedFields]) => {
        return filteredBySearch.filter((schema) => !selectedFields.map((f) => f.fieldId).includes(schema.fieldId));
      }),
    );
  }

  private addToSelectedFields(selectedFieldId: string): void {
    const selectedFieldIds = this.selectedFieldIds$$.value;
    this.fieldSchemas$.pipe(take(1)).subscribe((fieldSchemas) => {
      const schemaExistsInOptions = !!fieldSchemas.find((f) => f.fieldId === selectedFieldId);
      if (schemaExistsInOptions) {
        selectedFieldIds.push(selectedFieldId);
        this.selectedFieldIds$$.next(selectedFieldIds);
      }
    });
    this.fieldSearch.setValue(null);
  }
}
